// =============================================================================
// SWISS DESIGN ANIMATION SYSTEM
// Subtle, professional springs with minimal bounce
// =============================================================================

// Swiss spring - subtle, professional (primary animation)
export const swissSpring = {
  type: "spring" as const,
  duration: 0.5,
  bounce: 0.05  // Minimal bounce for precision
};

// =============================================================================
// PREMIUM ANIMATION VARIANTS
// Elevated animations for premium visual effects
// =============================================================================

// Premium reveal - blur + fade + translate for hero entrances
export const premiumReveal = {
  hidden: {
    opacity: 0,
    y: 30,
    filter: 'blur(10px)'
  },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      type: "spring" as const,
      duration: 0.8,
      bounce: 0.1
    }
  }
};

// Snappy spring - tight interactions for buttons and micro-interactions
export const snappySpring = {
  type: "spring" as const,
  stiffness: 400,
  damping: 30
};

// Smooth spring - page transitions with minimal bounce
export const smoothSpring = {
  type: "spring" as const,
  duration: 0.6,
  bounce: 0.05
};

// Card hover lift - subtle elevation on hover
export const cardHoverLift = {
  y: -4,
  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
  transition: {
    type: "spring" as const,
    stiffness: 400,
    damping: 25
  }
};

// Swiss reveal - for scroll-triggered content
export const swissReveal = {
  type: "spring" as const,
  duration: 0.6,
  bounce: 0.08
};

// Swiss stagger - tighter timing for grouped elements
export const swissStagger = {
  staggerChildren: 0.08,
  delayChildren: 0.1
};

// Swiss fade - clean opacity transition
export const swissFade = {
  type: "tween" as const,
  duration: 0.4,
  ease: [0.25, 0.1, 0.25, 1] // cubic-bezier for smooth fade
};

// =============================================================================
// LEGACY ANIMATIONS (kept for backwards compatibility)
// =============================================================================

// Calm spring animation for coaching brand
export const calmSpring = {
  type: "spring" as const,
  stiffness: 100,
  damping: 20,
  mass: 1
};

// Gentle reveal for section transitions (duration: 0.6s, bounce: 0.1)
export const gentleReveal = {
  type: "spring" as const,
  duration: 0.6,
  bounce: 0.1
};

// Swiss section variants - cleaner reveal with less bounce
export const swissSectionVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      ...swissReveal,
      staggerChildren: 0.08
    }
  }
};

// Swiss card variants - subtle scale + fade
export const swissCardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: swissSpring
  }
};

// Swiss stagger container
export const swissStaggerContainer = {
  hidden: {},
  visible: {
    transition: swissStagger
  }
};

// Section reveal variants for whileInView animations
export const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      ...gentleReveal,
      staggerChildren: 0.1
    }
  }
};

// Card variants for staggered animations
export const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: gentleReveal }
};

// Page transition config
export const pageTransition = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.4, ease: "easeInOut" }
};

// Fade in from bottom
export const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: gentleReveal }
};

// Scale fade for cards
export const scaleFade = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: gentleReveal }
};

// Stagger container
export const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

// Stagger item (for use with staggerContainer)
export const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: gentleReveal }
};

// Scale stagger item (scale + fade for cards)
export const scaleStaggerItem = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: gentleReveal }
};

// Simple fade in
export const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5, ease: "easeOut" } }
};

// Hero word stagger animation
export const heroContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.3 }
  }
};

export const heroWordVariants = {
  hidden: { opacity: 0, y: 30, rotateX: -20 },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    transition: { type: "spring", stiffness: 100, damping: 12 }
  }
};

// Floating blob animation config (for use with motion animate prop)
export const floatingBlobAnimation = {
  x: [0, 30, 0],
  y: [0, -20, 0],
  scale: [1, 1.1, 1],
};

export const floatingBlobTransition = {
  duration: 20,
  repeat: Infinity,
  ease: "easeInOut" as const
};

// Testimonial card variants
export const testimonialCardVariants = {
  hidden: { opacity: 0, x: 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 100, damping: 20 }
  }
};

// Journey step transition variants (Typeform style)
export const journeyStepVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 30 }
  },
  exit: {
    opacity: 0,
    y: -40,
    transition: { duration: 0.2 }
  }
};

// Journey option stagger
export const journeyOptionContainerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.05, delayChildren: 0.2 }
  }
};

export const journeyOptionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 25 }
  }
};

// =============================================================================
// RE-EXPORT FLUID ALCHEMIST ANIMATIONS
// For convenient imports from a single location
// =============================================================================

export {
  goldenSpring,
  swiftEase,
  magneticSpring,
  tiltSpring,
  pageStackVariants,
  curtainRevealContainer,
  curtainRevealWord,
  curtainRevealLine,
  dockItemVariants,
  dockLampVariants,
  tiltCardVariants,
  magneticButtonVariants,
  scrollProgressSpring,
  monumentalReveal,
  fadeInBlur,
  scaleIn,
  slideIn,
  fluidStagger,
  fluidStaggerContainer
} from './fluid-animations';
